import { useState } from 'react'
import { AlertTriangle, Check, Loader2, RotateCw } from 'lucide-react'

import type { Invoice } from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'
import { Button } from '@/shared/ui/primitives'

export type InvoiceProviderVoidPanelProps = {
  invoice: Invoice
  onRetry: () => Promise<void>
  onAcknowledge: () => Promise<void>
}

export function InvoiceProviderVoidPanel({
  invoice,
  onRetry,
  onAcknowledge,
}: InvoiceProviderVoidPanelProps) {
  const [pending, setPending] = useState<'retry' | 'acknowledge' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = async (kind: 'retry' | 'acknowledge', action: () => Promise<void>) => {
    setError(null)
    setPending(kind)
    try {
      await action()
    } catch (err) {
      const message = reportError(err, {
        fallbackMessage:
          kind === 'retry'
            ? 'Could not reach Stripe. Please try again.'
            : 'Failed to mark this as handled. Please try again.',
      })
      setError(message)
    } finally {
      setPending(null)
    }
  }

  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4">
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-destructive" />
        <h4 className="text-sm font-semibold text-destructive">
          Still live in Stripe &mdash; customer can still pay it
        </h4>
      </div>
      <p className="mb-1 text-xs text-muted-foreground">
        This invoice was voided in BlindBook, but Stripe did not accept the void. The
        Stripe invoice is still open and payable, so the customer may be charged for an
        amount you no longer expect.
      </p>
      <p className="mb-1 text-xs text-muted-foreground">
        Because of this, the updated invoice was sent to your customer{' '}
        <span className="font-medium text-foreground">without card payment</span> &mdash;
        they can still pay it another way.
      </p>
      {invoice.providerVoidError ? (
        <p className="mb-1 text-xs text-muted-foreground">
          Stripe said:{' '}
          <span className="font-medium text-foreground">{invoice.providerVoidError}</span>
        </p>
      ) : null}
      {invoice.providerVoidEscalatedAt ? (
        <p className="mb-3 text-xs text-muted-foreground">
          The void failed again on Stripe and BlindBook has been notified &mdash; we will
          void it from our side. Please reach out to your customer as well so they do not
          pay it in the meantime.
        </p>
      ) : (
        <p className="mb-3 text-xs text-muted-foreground">
          Retry the void to try again. If you have already settled this with the customer
          another way, mark it handled to dismiss this warning.
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => void run('retry', onRetry)}
          disabled={pending !== null}
        >
          {pending === 'retry' ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <RotateCw className="size-4" />
          )}
          Retry void
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void run('acknowledge', onAcknowledge)}
          disabled={pending !== null}
        >
          {pending === 'acknowledge' ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
          Mark handled
        </Button>
      </div>
      {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
    </div>
  )
}
